import React, { useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import {
  CheckCircle2,
  Copy,
  Download,
  Lock,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { enneagramQuestions, enneagramTypeDescriptions } from '../data/enneagramQuestions';
import { activateMembershipCode, getStoredUser, isProMember } from '../lib/api';
import XuanYuanTopBar from './XuanYuanTopBar';
import XuanYuanReportFooter from './XuanYuanReportFooter';

interface EnneagramTestProps {
  onBack: () => void;
  onRequireLogin?: () => void;
  onActivated?: () => void;
  previewMode?: boolean;
  onBeforeShowResult?: (testId: string) => Promise<boolean>;
}

type EnneagramStep = 'quiz' | 'activation' | 'result';

interface EnneagramResult {
  topType: number;
  secondType: number;
  scores: Record<number, number>;
}

const WECHAT_ID = 'dao6257';
const SITE_URL = 'https://qimen.aifashou.cn';
const STORAGE_UNLOCK = 'other_app_enneagram_unlock_v2';
const STORAGE_PENDING = 'other_app_enneagram_pending_v2';
const STORAGE_RESULT = 'other_app_enneagram_result_v2';

const TYPE_ROLES: Record<number, string> = {
  1: '标准修正者',
  2: '关系照料者',
  3: '目标成就者',
  4: '情感表达者',
  5: '知识观察者',
  6: '风险防御者',
  7: '体验开拓者',
  8: '边界掌控者',
  9: '和平调和者',
};

const TYPE_KEYWORDS: Record<number, string[]> = {
  1: ['原则', '自律', '修正', '标准'],
  2: ['助人', '连接', '付出', '温度'],
  3: ['效率', '成就', '目标', '表现'],
  4: ['情绪', '真实', '独特', '审美'],
  5: ['理性', '独立', '观察', '边界'],
  6: ['谨慎', '预案', '忠诚', '稳定'],
  7: ['乐观', '体验', '自由', '创意'],
  8: ['力量', '掌控', '果断', '保护'],
  9: ['和谐', '包容', '缓冲', '稳定'],
};

const PREVIEW_ENNEAGRAM_RESULT: EnneagramResult = {
  topType: 3,
  secondType: 8,
  scores: {
    1: 12,
    2: 11,
    3: 18,
    4: 9,
    5: 10,
    6: 12,
    7: 13,
    8: 16,
    9: 10,
  },
};

function loadBoolean(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function saveBoolean(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // noop
  }
}

function computeEnneagramResult(answers: number[]): EnneagramResult {
  const scores: Record<number, number> = {};
  for (let i = 1; i <= 9; i += 1) scores[i] = 0;
  enneagramQuestions.forEach((q, idx) => {
    const val = answers[idx];
    if (val >= 1 && val <= 5) scores[q.type] += val;
  });

  const ranked = Object.entries(scores)
    .map(([type, score]) => ({ type: Number(type), score }))
    .sort((a, b) => b.score - a.score);

  return {
    topType: ranked[0]?.type || 9,
    secondType: ranked[1]?.type || ranked[0]?.type || 9,
    scores,
  };
}

function loadSavedResult(): EnneagramResult | null {
  try {
    const raw = localStorage.getItem(STORAGE_RESULT);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EnneagramResult;
    if (parsed && parsed.topType && parsed.scores) return parsed;
    return null;
  } catch {
    return null;
  }
}

function saveResult(result: EnneagramResult): void {
  try {
    localStorage.setItem(STORAGE_RESULT, JSON.stringify(result));
  } catch {
    // noop
  }
}

function buildPortraitText(topType: number, secondType: number): string {
  const topRole = TYPE_ROLES[topType] || `${topType}号类型`;
  const secondRole = TYPE_ROLES[secondType] || `${secondType}号类型`;
  return `你的人格主轴落在${topRole}，行动时会优先使用这类心理策略；同时带有${secondRole}的辅助风格，让你在不同关系和任务场景中具备额外的调节能力。`;
}

function buildInsights(topType: number): { title: string; content: string }[] {
  const role = TYPE_ROLES[topType] || `${topType}号类型`;
  const base = enneagramTypeDescriptions[topType] || '你具备明显的个体风格与价值追求。';

  const stress = topType === 3 || topType === 1 || topType === 8
    ? '你在高压下容易进入“控制节奏”模式。建议保留 10 分钟缓冲复盘，避免强推导致关系摩擦。'
    : '你在高压下容易进入“回避冲突”或“过度思考”模式。建议先写下最小行动，再开始推进。';

  const relation = topType === 2 || topType === 9 || topType === 4
    ? '你在关系里很重感受与连接。建议在照顾他人前先确认自己的边界和需求。'
    : '你在关系里强调结果与稳定。建议在决策前多问一句“对方当下的感受是什么”。';

  const growth = topType === 7 || topType === 5
    ? '成长关键是“稳定节奏 + 深度投入”。把注意力从多选项回收到一个长期主线。'
    : '成长关键是“弹性视角 + 允许试错”。在标准之外，刻意给自己留一段探索空间。';

  return [
    { title: `类型定位：${topType}号 · ${role}`, content: base },
    { title: '压力模式识别', content: stress },
    { title: '关系互动建议', content: relation },
    { title: '成长方向', content: growth },
  ];
}

const EnneagramTest: React.FC<EnneagramTestProps> = ({ onBack, onRequireLogin, onActivated, previewMode = false, onBeforeShowResult }) => {
  const [answers, setAnswers] = useState<number[]>(() => Array(enneagramQuestions.length).fill(0));
  const [index, setIndex] = useState(0);
  const [step, setStep] = useState<EnneagramStep>('quiz');
  const [result, setResult] = useState<EnneagramResult | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [pendingUnlock, setPendingUnlock] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [activationMsg, setActivationMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const reportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void QRCode.toDataURL(SITE_URL, {
      width: 140,
      margin: 1,
      color: { dark: '#1f3e5e', light: '#0000' },
    })
      .then((url) => setQrDataUrl(url))
      .catch(() => setQrDataUrl(''));
  }, []);

  useEffect(() => {
    if (previewMode) {
      setUnlocked(true);
      setPendingUnlock(false);
      setResult(PREVIEW_ENNEAGRAM_RESULT);
      setActivationMsg(null);
      setStep('result');
      return;
    }

    const unlockedStored = loadBoolean(STORAGE_UNLOCK);
    const pendingStored = loadBoolean(STORAGE_PENDING);
    const memberUnlocked = isProMember(getStoredUser());
    const effectiveUnlocked = unlockedStored || memberUnlocked;

    if (memberUnlocked && !unlockedStored) saveBoolean(STORAGE_UNLOCK, true);
    if (memberUnlocked && pendingStored) saveBoolean(STORAGE_PENDING, false);

    const saved = loadSavedResult();
    setUnlocked(effectiveUnlocked);
    setPendingUnlock(pendingStored && !effectiveUnlocked);
    setResult(saved);

    if ((pendingStored && !effectiveUnlocked) || (!!saved && !effectiveUnlocked)) {
      setStep('activation');
      return;
    }

    if (saved && effectiveUnlocked) {
      setStep('result');
      return;
    }

    setStep('quiz');
  }, [previewMode]);

  const current = enneagramQuestions[Math.max(0, Math.min(index, enneagramQuestions.length - 1))];
  const answeredCount = answers.filter((x) => x >= 1).length;
  const progress = Math.round((answeredCount / enneagramQuestions.length) * 100);

  const rankedTypes = useMemo(() => {
    if (!result) return [];
    return (Object.entries(result.scores) as Array<[string, number]>)
      .map(([type, score]) => ({ type: Number(type), score: Number(score) }))
      .sort((a, b) => b.score - a.score);
  }, [result]);

  const keywords = useMemo(() => {
    if (!result) return [];
    const top = TYPE_KEYWORDS[result.topType] || [];
    const second = TYPE_KEYWORDS[result.secondType] || [];
    return [...top, ...second].slice(0, 8);
  }, [result]);

  const portraitText = useMemo(() => {
    if (!result) return '';
    return buildPortraitText(result.topType, result.secondType);
  }, [result]);

  const insights = useMemo(() => {
    if (!result) return [];
    return buildInsights(result.topType);
  }, [result]);

  const chooseScore = (value: 1 | 2 | 3 | 4 | 5) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const submitTest = () => {
    if (answeredCount < enneagramQuestions.length) {
      setActivationMsg({ ok: false, text: '请先完成全部 36 题。' });
      return;
    }

    const nextResult = computeEnneagramResult(answers);
    setResult(nextResult);
    saveResult(nextResult);

    if (unlocked) {
      setStep('result');
      return;
    }
    void (async () => {
      const canShowResult = onBeforeShowResult ? await onBeforeShowResult('enneagram') : false;
      if (canShowResult) {
        setPendingUnlock(false);
        saveBoolean(STORAGE_PENDING, false);
        setActivationMsg(null);
        setStep('result');
        return;
      }
      setPendingUnlock(true);
      saveBoolean(STORAGE_PENDING, true);
      setActivationMsg(null);
      setStep('activation');
    })();
  };

  const resetAll = () => {
    setAnswers(Array(enneagramQuestions.length).fill(0));
    setIndex(0);
    setActivationMsg(null);
    if (!unlocked) {
      setPendingUnlock(false);
      saveBoolean(STORAGE_PENDING, false);
    }
    setStep('quiz');
  };

  const copyWechat = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(WECHAT_ID);
      }
      setActivationMsg({ ok: true, text: '微信号已复制。' });
    } catch {
      setActivationMsg({ ok: false, text: `复制失败，请手动添加 ${WECHAT_ID}` });
    }
  };

  const unlockReport = async () => {
    const user = getStoredUser();
    if (!user?.userId) {
      setActivationMsg({ ok: false, text: '请先登录后再激活。' });
      if (onRequireLogin) onRequireLogin();
      return;
    }

    const normalized = codeInput.trim().toUpperCase();
    if (normalized.length < 6) {
      setActivationMsg({ ok: false, text: '请输入有效激活码（至少 6 位）。' });
      return;
    }

    setActivationMsg({ ok: true, text: '激活中，请稍候…' });
    try {
      await activateMembershipCode(normalized);
      setUnlocked(true);
      setPendingUnlock(false);
      saveBoolean(STORAGE_UNLOCK, true);
      saveBoolean(STORAGE_PENDING, false);
      setActivationMsg({ ok: true, text: '激活成功，已解锁九型人格完整报告。' });
      if (onActivated) onActivated();
      setTimeout(() => setStep(result ? 'result' : 'quiz'), 250);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '激活失败';
      setActivationMsg({ ok: false, text: msg });
      if (msg.includes('请先登录') && onRequireLogin) onRequireLogin();
    }
  };

  const exportImage = async () => {
    if (!reportRef.current) return;
    setIsExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: '#edf3f8',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `九型人格_${result?.topType || 'top'}号_报告.png`;
      link.click();
    } catch {
      setActivationMsg({ ok: false, text: '导出失败，请稍后重试。' });
    } finally {
      setIsExporting(false);
    }
  };

  const startFormalTest = () => {
    if (!previewMode) return;
    setAnswers(Array(enneagramQuestions.length).fill(0));
    setIndex(0);
    setActivationMsg(null);
    setResult(null);
    setUnlocked(isProMember(getStoredUser()) || loadBoolean(STORAGE_UNLOCK));
    setPendingUnlock(false);
    setStep('quiz');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#eef4fb] to-[#e4edf7] p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <XuanYuanTopBar
          title={previewMode ? '轩辕国学·九型人格测试样例' : '轩辕国学·九型人格测试'}
          onBack={onBack}
          variant="light"
          sticky={false}
          maxWidthClassName="max-w-4xl"
        />

        {step === 'quiz' && (
          <div className="bg-white/90 backdrop-blur border border-sky-200 rounded-3xl p-5 sm:p-6 shadow-xl">
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>进度：{answeredCount}/{enneagramQuestions.length}</span>
              <span>{progress}%</span>
            </div>
            <div className="mt-2 h-2 bg-sky-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all" style={{ width: `${progress}%` }} />
            </div>

            <div className="mt-6 rounded-2xl border border-sky-100 bg-gradient-to-br from-white to-sky-50 p-5">
              <p className="text-xs text-slate-500 mb-2">第 {index + 1} 题</p>
              <p className="text-lg font-semibold text-slate-900 leading-8">{current.text}</p>
              <p className="text-sm text-slate-500 mt-2">请选择符合程度（1-5）</p>

              <div className="mt-4 grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={`score-${value}`}
                    onClick={(event) => {
                      event.currentTarget.blur();
                      chooseScore(value as 1 | 2 | 3 | 4 | 5);
                    }}
                    className={`rounded-lg border py-2 text-sm font-semibold ${
                      answers[index] === value
                        ? 'border-sky-500 bg-sky-100 text-sky-700'
                        : 'border-slate-200 text-slate-600 active:border-sky-300 active:bg-sky-50/60 md:hover:border-sky-300 bg-white'
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="mt-2 text-xs text-slate-500">1=完全不符合，5=非常符合</div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <button
                onClick={() => setIndex((v) => Math.max(0, v - 1))}
                disabled={index === 0}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 disabled:opacity-40"
              >
                上一题
              </button>
              {index < enneagramQuestions.length - 1 ? (
                <button
                  onClick={() => setIndex((v) => Math.min(enneagramQuestions.length - 1, v + 1))}
                  className="px-4 py-2 rounded-lg bg-slate-900 text-white"
                >
                  下一题
                </button>
              ) : (
                <button onClick={submitTest} className="px-4 py-2 rounded-lg bg-cyan-600 text-white">
                  提交并生成结果
                </button>
              )}
              <button onClick={resetAll} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700">
                <RefreshCw className="w-4 h-4 inline mr-1" />
                重做
              </button>
            </div>
          </div>
        )}

        {step === 'activation' && (
          <div className="bg-white/90 backdrop-blur border border-sky-200 rounded-3xl p-5 sm:p-6 shadow-xl">
            <div className="flex items-center gap-2 text-cyan-700">
              <Lock className="w-5 h-5" />
              <h2 className="text-lg font-bold">已完成测试，请激活后查看完整报告</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600 leading-7">
              报告已生成。请添加若轩老师微信，支付 19.9 元获取激活码。
            </p>
            <button onClick={copyWechat} className="mt-3 inline-flex items-center gap-1 text-indigo-600 font-semibold">
              微信：{WECHAT_ID}
              <Copy className="w-3 h-3" />
            </button>

            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                placeholder="请输入激活码"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
              />
              <button onClick={() => { void unlockReport(); }} className="px-4 py-2 rounded-lg bg-slate-900 text-white">
                立即激活
              </button>
            </div>

            {activationMsg && (
              <div className={`mt-3 text-sm ${activationMsg.ok ? 'text-emerald-600' : 'text-rose-600'}`}>
                {activationMsg.text}
              </div>
            )}
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-slate-600">九型主类型：<span className="font-bold text-slate-900">{result.topType}号</span> · {TYPE_ROLES[result.topType]}{previewMode ? ' · 报告样例' : ''}</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { void exportImage(); }}
                  disabled={isExporting}
                  className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-60"
                >
                  <Download className="w-4 h-4 inline mr-1" />
                  {isExporting ? '导出中...' : '导出报告图片'}
                </button>
                {previewMode ? (
                  <button onClick={startFormalTest} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm">开始正式测试</button>
                ) : (
                  <button onClick={resetAll} className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm">重新测试</button>
                )}
              </div>
            </div>

            <div
              ref={reportRef}
              className="rounded-3xl border border-[#c8d7e8] bg-[#edf3f8] shadow-[0_18px_40px_rgba(55,83,120,0.16)] p-5 sm:p-8"
            >
              <div className="text-center mb-6">
                <p className="text-xs tracking-[0.18em] text-[#416387] font-semibold">ENNEAGRAM REPORT</p>
                <h2 className="text-3xl font-black text-[#1e3248] mt-2">九型人格画像报告</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-5">
                <div className="rounded-2xl border border-[#c8d7e8] bg-white/75 p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#285d8d] to-[#35a2c9] text-white flex items-center justify-center text-2xl font-black">
                      {result.topType}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">主类型 / 副类型</p>
                      <p className="text-xl font-bold text-[#1e3248]">{result.topType}号 · {result.secondType}号</p>
                    </div>
                  </div>
                  <div className="mt-4 rounded-xl bg-[#e8f1f8] border border-[#d5e3f0] p-3">
                    <p className="text-sm font-semibold text-[#34597c] mb-1">人物画像</p>
                    <p className="text-sm leading-7 text-[#274767]">{portraitText}</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-[#c8d7e8] bg-white/75 p-5">
                  <p className="text-sm font-semibold text-[#34597c] mb-2 flex items-center gap-1">
                    <Sparkles className="w-4 h-4" /> 关键词分析
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {keywords.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 rounded-full text-xs border border-[#bfd4e8] bg-[#f1f8ff] text-[#2d587e] font-semibold">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 text-sm leading-7 text-[#274767]">
                    {enneagramTypeDescriptions[result.topType] || '你具备明显的人格主轴与辅助策略，建议结合场景灵活调用。'}
                  </p>
                </div>
              </div>

              <div className="rounded-2xl border border-[#c8d7e8] bg-white/75 p-5 mb-5">
                <p className="text-sm font-semibold text-[#34597c] mb-3">九型分数分布</p>
                <div className="space-y-3">
                  {rankedTypes.map((item) => {
                    const ratio = Math.max(4, Math.min(100, Math.round((item.score / 20) * 100)));
                    return (
                      <div key={`type-${item.type}`}>
                        <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                          <span>{item.type}号 · {TYPE_ROLES[item.type]}</span>
                          <span>{item.score}分</span>
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-600" style={{ width: `${ratio}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-3">
                {insights.map((insight) => (
                  <div key={insight.title} className="rounded-xl border border-[#c8d7e8] bg-white/75 p-4">
                    <h3 className="text-base font-bold text-[#1e3248]">{insight.title}</h3>
                    <p className="mt-1 text-sm leading-7 text-[#274767]">{insight.content}</p>
                  </div>
                ))}
              </div>

              <XuanYuanReportFooter
                qrDataUrl={qrDataUrl}
                textClassName="text-[#4f6f8e]"
                emphasizeClassName="text-[#1e3248]"
                qrBorderClassName="border-[#c8d7e8]"
              />
            </div>

            <p className="text-xs text-slate-500 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> 报告已生成，可直接导出图片分享。
            </p>
          </div>
        )}

        {!unlocked && pendingUnlock && step !== 'activation' && (
          <div className="mt-4 text-xs text-slate-500">未完成激活时，下次进入将自动停留在激活页。</div>
        )}
      </div>
    </div>
  );
};

export default EnneagramTest;
